// src/pages/StudyPage.tsx
// =====================================================================
// STUDYPAGE REFATORADO - IDENTIDADE GRAIA
// ✅ CORRIGIDO: Inicialização simplificada, hook gerencia tudo
// =====================================================================

import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { BarChart3, Moon, Sun, BookX } from 'lucide-react';
import './StudyPage.css';

// HOOKS ISOLADOS
import { useStudyAlgorithm } from '../hooks/useStudyAlgorithm';
import { useSessionStats } from '../hooks/useSessionStats';
import { useStudyMode } from '../hooks/useStudyMode';
import { useAuth } from '../contexts/AuthContext';
import { useAntiPiracy } from '../hooks/useAntiPiracy';
import { useTheme } from '../contexts/ThemeContext';
import { useStudyOnboarding } from '../hooks/useStudyOnboarding';
import { StudyOnboarding } from '../components/study/StudyOnboarding';
import { useSubdeckProgress } from '../hooks/useSubdeckProgress';
import { useCardContentDensity } from '../hooks/useCardContentDensity';

// COMPONENTES ISOLADOS
import { StudySidebarPanel } from '../components/study/StudySidebarPanel';
import CardActionsMenu from '../components/study/CardActionsMenu';
import Toast from '../components/Toast';
import FocusTimer from '../components/study/FocusTimer';
import ReportErrorModal from '../components/study/ReportErrorModal';
import QuickAddModal from '../components/study/QuickAddModal';
import { ImageZoomModal } from '../components/CardEditor/ImageZoomModal';
import { CardMediaRenderer } from '../components/study/CardMediaRenderer';
import FocusExitModal from '../components/study/FocusExitModal';
import { SupportContent } from '../components/study/SupportContent';
import '../components/study/StudySidebarPanel.css';
import ReferencesStudy from '../components/study/ReferencesStudy';
import SampleExhaustedModal from '../components/study/SampleExhaustedModal';

// INTERFACES
interface ProgressStats {
    totalCards: number;
    masteredCards: number;
    masteryPercentage: number;
    statusDistribution: {
        new: number;
        learning: number;
        reviewing: number;
        mastered: number;
    };
    accuracy: number;
    needsAttention: number;
    progressText: string;
    needsReview: number;
    deckName?: string;
    subdeckName?: string;
}

interface StudyPageProps {
    isCustomDeck?: boolean;
    isUserContent?: boolean;
}

// =====================================================================
// COMPONENTE: Histórico de avaliações do card atual (últimas 5)
// =====================================================================

interface CardRatingHistoryProps {
    history: number[];
}

const RATING_LABELS: Record<number, string> = {
    1: 'Muito Difícil',
    2: 'Difícil',
    3: 'Bom',
    4: 'Fácil',
    5: 'Muito Fácil',
};

/* DEPOIS — animação só nos dots via wrapper com key */
const CardRatingHistory: React.FC<CardRatingHistoryProps> = ({ history }) => {
    const last5 = history.slice(-5);

    return (
        <div className="card-history-row">
            <span className="card-history-label">Histórico</span>
            <div className="card-history-dots-animated" key={last5.join(',')}>
                {last5.length === 0 ? (
                    <span className="card-history-empty">—</span>
                ) : (
                    last5.map((rating, i) => (
                        <React.Fragment key={i}>
                            {i > 0 && <span className="card-history-sep">›</span>}
                            <div
                                className={`card-history-dot rating-dot-${rating}`}
                                data-tooltip={RATING_LABELS[rating]}
                            >
                                {rating}
                            </div>
                        </React.Fragment>
                    ))
                )}
            </div>
        </div>
    );
};

// =====================================================================
// COMPONENTE PRINCIPAL
// =====================================================================

const StudyPage: React.FC<StudyPageProps> = ({ isCustomDeck = false, isUserContent: isUserContentProp = false }) => {
    const { deckId, subdeckId } = useParams<{ deckId: string; subdeckId: string }>();
    const navigate = useNavigate();
    const location = useLocation();
    const { isAdmin } = useAuth();
    const { theme, toggleTheme, setTheme } = useTheme();

    // ✅ ANTIPIRATARIA
    useAntiPiracy(!isAdmin);

    // ✅ MODO DE ESTUDO
    const {
        isFocusMode,
        isReviewMode,
        focusTimeRemaining,
        confirmExit,
        shouldShowExitWarning,
        addFocusTime
    } = useStudyMode();

    // ✅ CONFIGURAÇÃO DOS MODOS
    const isCustomPath = isCustomDeck;
    const isSavedMode = window.location.pathname.startsWith('/study/saved');
    const isCustomStudy = location.pathname === '/study/custom';
    const isUserContent = isUserContentProp || location.pathname.startsWith('/study/user/');

    // Detectar contexto Ryan via location.state
    const fromRyan = (location.state as any)?.fromRyan === true;
    const ryanReturnPath = (location.state as any)?.returnPath as string | undefined;
    const ryanReturnMode = (location.state as any)?.ryanReturnMode as string | undefined;
    const ryanReturnView = (location.state as any)?.ryanReturnView as string | undefined;
    const ryanReturnFolderId = (location.state as any)?.ryanReturnFolderId as string | undefined;
    const ryanSpaceName = (location.state as any)?.spaceName as string | undefined;
    const ryanSpaceId = (location.state as any)?.spaceId as string | undefined;
    const customStudyLabel = (location.state as any)?.customStudyLabel as string | undefined;

    // Aplica e persiste o contexto de tema no mount: fromRyan tem prioridade sobre
    // sessionStorage; se não há nenhum sinal Ryan, define 'enem' explicitamente.
    useEffect(() => {
        const isRyan = fromRyan || sessionStorage.getItem('graia_theme_context') === 'ryan';
        sessionStorage.setItem('graia_theme_context', isRyan ? 'ryan' : 'enem');
        if (isRyan) {
            document.body.classList.add('theme-ryan');
        } else {
            document.body.classList.remove('theme-ryan');
        }

        // ✅ Nenhuma outra página da plataforma Ryan tem modo escuro configurado
        // (ex.: RyanFieldPage quebra visualmente em dark). O StudyPage é a
        // única tela do Ryan com dark mode, então ao sair dela — por
        // qualquer caminho, incluindo botão voltar do navegador — o tema
        // volta para claro automaticamente para não vazar dark pro resto do Ryan.
        return () => {
            if (isRyan) {
                setTheme('light');
            }
        };
    }, []); // eslint-disable-line react-hooks/exhaustive-deps

    const customPool = React.useMemo(() => {
        if (!isCustomStudy) return undefined;
        try {
            const raw = localStorage.getItem('customStudyPool');
            return raw ? JSON.parse(raw) : undefined;
        } catch {
            return undefined;
        }
    }, [isCustomStudy]);

    // =====================================================================
    // ✅ HOOK DE ALGORITMO - AGORA SE AUTO-INICIALIZA
    // =====================================================================

    const algorithm = useStudyAlgorithm({
        deckId: deckId || '',
        subdeckId: subdeckId || '',
        isCustomDeck: isCustomPath,
        isSavedMode,
        isReviewMode,
        isCustomStudy,
        customPool,
        isUserContent,
        onBeforeLoadNextCard: () => {
            setShowSupportContent(false);
        },
    });

    // =====================================================================
    // ✅ HOOK DE ESTATÍSTICAS
    // =====================================================================

    const stats = useSessionStats({
        deckId: deckId || '',
        subdeckId: subdeckId || '',
        isReviewMode,
        isSavedMode
    });

    const onboarding = useStudyOnboarding();

    // =====================================================================
    // ESTADOS DA UI
    // =====================================================================

    const [deckName, setDeckName] = useState('');
    const [subdeckName, setSubdeckName] = useState('');
    const [subdeckReferences, setSubdeckReferences] = useState('');
    const [isFlipped, setIsFlipped] = useState(false);
    const [progressStats, setProgressStats] = useState<ProgressStats | null>(null);
    const [showSupportContent, setShowSupportContent] = useState(false);
    const [zoomImage, setZoomImage] = useState<string | null>(null);
    const [zoomScale, setZoomScale] = useState<number>(1);
    const [showReferencesModal, setShowReferencesModal] = useState(false);
    const [showSampleExhaustedModal, setShowSampleExhaustedModal] = useState(false);
    const [showFocusExitModal, setShowFocusExitModal] = useState(false);
    const [showErrorModal, setShowErrorModal] = useState(false);
    const [showQuickAddModal, setShowQuickAddModal] = useState(false);
    const [toast, setToast] = useState<{ message: string; type: 'success' | 'error' | 'info' } | null>(null);
    const [supportZoomImage, setSupportZoomImage] = useState<string | null>(null);
    const [supportZoomScale, setSupportZoomScale] = useState(1);
    const [isSidebarOpen, setIsSidebarOpen] = useState(false);
    const [isTransitioning, setIsTransitioning] = useState(false);
    const handleCloseToast = useCallback(() => setToast(null), []);

    // ✅ Ajuste adaptativo de fonte/posicionamento em mobile/tablet, baseado na
    // altura real do conteúdo renderizado (ver useCardContentDensity + os
    // blocos de media query em StudyPage.css que consomem --gr-empty).
    // Desktop não é afetado: as regras que leem essa variável só existem
    // dentro das media queries de tablet/mobile.
    const { frontContentRef, backContentRef, remeasure } = useCardContentDensity();
    const [freeCardsLimit, setFreeCardsLimit] = useState<number | null>(null);
    const [freeCardsUsed, setFreeCardsUsed] = useState<number>(0);

    // NOVO: delta acumulado baseado em contribuição real por card
    const [masteryDelta, setMasteryDelta] = useState(0);
    // totalCards carregado do servidor — necessário para calcular contribuição real
    const totalCardsRef = useRef<number>(0);

    const isPurchasedDeck = isAdmin || isUserContent || ((location.state as any)?.purchased === true);

    // Valor exibido na barra — nunca ultrapassa 100, nunca fica negativo
    const displayedMastery = progressStats
        ? Math.min(100, Math.max(0, progressStats.masteryPercentage + masteryDelta))
        : 0;

    const subdeckProgress = useSubdeckProgress(
        deckId || '',
        subdeckId || '',
        isCustomPath,
        displayedMastery,
        stats.precision,
        stats.sessionStats.cardsStudied
    );

    // MANTIDO — salvar no sessionStorage a cada mudança de domínio exibido
    useEffect(() => {
        if (!deckId || !subdeckId || !progressStats || isUserContent) return;
        const key = `displayed_mastery_${deckId}_${subdeckId}`;
        const value = {
            mastery: Math.round(displayedMastery),
            savedAt: Date.now()
        };
        sessionStorage.setItem(key, JSON.stringify(value));
    }, [displayedMastery, deckId, subdeckId, progressStats, isUserContent]);


    // =====================================================================
    // ✅ CARREGAR DADOS COMPLEMENTARES (não interferem no card)
    // =====================================================================

    const loadProgressStats = useCallback(async () => {
        try {
            const token = localStorage.getItem('accessToken');

            const statsUrl = isUserContent
                ? `/api/user-content/study/${deckId}/stats`
                : isCustomPath
                    ? `/api/progress/custom-stats/${deckId}/${subdeckId}`
                    : `/api/progress/stats/${deckId}/${subdeckId}`;

            const response = await fetch(statsUrl, {
                headers: { Authorization: `Bearer ${token}` }
            });

            if (response.ok) {
                const stats = await response.json();
                setProgressStats(stats);

                if (stats.totalCards && stats.totalCards > 0) {
                    totalCardsRef.current = stats.totalCards;
                }

                if (stats.deckName) setDeckName(stats.deckName);
                if (stats.subdeckName !== undefined) setSubdeckName(stats.subdeckName);
            }
        } catch (error) {
            console.error('❌ Erro ao carregar estatísticas:', error);
        }
    }, [deckId, subdeckId, isCustomPath, isUserContent]);

    const loadDeckNames = useCallback(async () => {
        if (isSavedMode || isCustomStudy) return;
        if (isUserContent) {
            // Stats endpoint already sets deckName; no need for a separate names fetch
            return;
        }

        try {
            const token = localStorage.getItem('accessToken');

            const endpoint = isCustomPath
                ? `/api/custom-decks/${deckId}/subdecks/${subdeckId}`
                : `/api/decks/${deckId}/subdecks/${subdeckId}`;

            const response = await fetch(endpoint, {
                headers: { Authorization: `Bearer ${token}` }
            });

            if (response.ok) {
                const data = await response.json();
                setDeckName(data.deckName || '');
                setSubdeckName(data.subdeck?.name || data.subdeckName || '');
                setSubdeckReferences(data.subdeck?.references || '');
            }
        } catch (error) {
            console.error('❌ Erro ao carregar nomes:', error);
        }
    }, [deckId, subdeckId, isSavedMode, isCustomStudy, isCustomPath, isUserContent]);

    // ✅ Carregar estatísticas de progresso (sidebar)
    useEffect(() => {
        if (!isCustomStudy && !isSavedMode && deckId && (subdeckId || isUserContent) && !isCustomPath) {
            loadProgressStats();
        }
    }, [deckId, subdeckId, isCustomStudy, isSavedMode, isCustomPath, isUserContent, loadProgressStats]);

    // Carregar status da amostra ao entrar (não aplicável para conteúdo do usuário)
    useEffect(() => {
        if (isAdmin || isPurchasedDeck || isUserContent || !deckId || !subdeckId) return;

        const token = localStorage.getItem('accessToken');
        fetch(`/api/admin/free-config/status/${deckId}/${subdeckId}?isCustom=${isCustomPath}`, {
            headers: { Authorization: `Bearer ${token}` }
        })
            .then(res => res.ok ? res.json() : null)
            .then(data => {
                if (!data) return;
                setFreeCardsLimit(data.freeConfig?.freeCardsLimit || null);
                setFreeCardsUsed(data.cardsViewed || 0);
            })
            .catch(() => { });
    }, [deckId, subdeckId, isAdmin, isPurchasedDeck, isCustomPath, isUserContent]);

    // Reconciliar displayedMastery com o servidor a cada 5 respostas
    useEffect(() => {
        if (stats.sessionStats.cardsStudied > 0
            && stats.sessionStats.cardsStudied % 5 === 0
            && !isCustomStudy && !isSavedMode
            && deckId && (subdeckId || isUserContent)) {

            const token = localStorage.getItem('accessToken');
            const statsUrl = isUserContent
                ? `/api/user-content/study/${deckId}/stats`
                : isCustomPath
                    ? `/api/progress/custom-stats/${deckId}/${subdeckId}`
                    : `/api/progress/stats/${deckId}/${subdeckId}`;

            fetch(statsUrl, { headers: { Authorization: `Bearer ${token}` } })
                .then(r => r.ok ? r.json() : null)
                .then(freshStats => {
                    if (!freshStats) return;
                    setProgressStats(freshStats);
                    setMasteryDelta(0);
                    if (freshStats.totalCards > 0) {
                        totalCardsRef.current = freshStats.totalCards;
                    }
                })
                .catch(() => { });
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [stats.sessionStats.cardsStudied]);


    // ✅ Carregar nomes do deck/subdeck
    useEffect(() => {
        if (deckId && (subdeckId || isUserContent) && !isSavedMode && !isCustomStudy) {
            loadDeckNames();
        }
    }, [deckId, subdeckId, isSavedMode, isCustomStudy, isUserContent, loadDeckNames]);

    // =====================================================================
    // FUNÇÕES DE CARREGAMENTO
    // =====================================================================

    const loadSavedCards = async () => {
        try {
            const token = localStorage.getItem('accessToken');
            const response = await fetch('/api/saved-cards', {
                headers: { Authorization: `Bearer ${token}` }
            });

            if (response.ok) {
                const data = await response.json();
                console.log('✅ Cards salvos recarregados:', data.savedCards.length);
            }
        } catch (error) {
            console.error('❌ Erro ao recarregar cards salvos:', error);
        }
    };

    // =====================================================================
    // HANDLERS DE INTERAÇÃO
    // =====================================================================

    const handleAnswer = async (rating: number) => {
        onboarding.onCardAnswered();

        // ✅ ADICIONAR ESTE BLOCO AQUI
        if (!isAdmin && !isPurchasedDeck && deckId && subdeckId) {
            const token = localStorage.getItem('accessToken');
            fetch('/api/admin/free-config/record-view', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
                body: JSON.stringify({
                    deckId,
                    subdeckId,
                    cardsViewed: 1,
                    isCustom: isCustomPath
                })
            }).then(res => res.json()).then(data => {
                setFreeCardsUsed(prev => prev + 1);
                if (data.exhausted) {
                    sessionStorage.setItem(`free_exhausted_${deckId}_${subdeckId}`, 'true');
                    setShowSampleExhaustedModal(true);  // ✅ modal, não redirect
                }
            }).catch(() => { });
        }

        // NOVO: calcular delta usando a mesma fórmula de exibição do backend
        // Desvinculada do masteryLevel interno do algoritmo
        if (algorithm.currentCard && totalCardsRef.current > 0) {

            // Mesma lógica do backend — sincronizada
            const BASE_SCORE: Record<number, number> = {
                1: 0, 2: 10, 3: 40, 4: 70, 5: 85
            };

            const getCardScore = (lastRating: number | null, consecutiveCorrect: number): number => {
                if (!lastRating || lastRating < 1 || lastRating > 5) return 0;
                // 2+ consecutivas boas = dominado = 100%
                if (consecutiveCorrect >= 2 && lastRating >= 4) return 100;
                return BASE_SCORE[lastRating] ?? 0;
            };

            const previousRating = algorithm.currentCard.progress.lastRating;
            const previousConsecutive = algorithm.currentCard.progress.repetitions ?? 0;

            // Score ANTES desta resposta
            const previousScore = getCardScore(previousRating, previousConsecutive);

            // Consecutivas APÓS esta resposta
            const newConsecutive = rating >= 4 ? previousConsecutive + 1 : 0;

            // Score DEPOIS desta resposta
            const newScore = getCardScore(rating, newConsecutive);

            const cardDelta = (newScore - previousScore) / totalCardsRef.current;

            setMasteryDelta(prev => {
                const next = prev + cardDelta;
                const currentBase = progressStats?.masteryPercentage ?? 0;
                // nunca negativo, nunca passa de 100%
                return Math.min(100 - currentBase, Math.max(-currentBase, next));
            });
        }



        if (!algorithm.currentCard) return;

        const cardSnapshot = algorithm.currentCard;

        stats.updateStats(
            cardSnapshot.card._id,
            rating,
            cardSnapshot.progress.status as any,
            cardSnapshot.progress.lastRating
        );

        // ✅ SEMPRE fade-out antes de trocar, independente do buffer
        setIsTransitioning(true);
        setIsFlipped(false);
        await new Promise(res => setTimeout(res, 100));
        await algorithm.handleAnswer(rating);
        setIsTransitioning(false);

        // ✅ Dispara animação de entrada
        setTimeout(() => {
            document.querySelectorAll('.card-content').forEach(el => {
                el.classList.add('content-entering');
                setTimeout(() => el.classList.remove('content-entering'), 200);
            });
        }, 10);
    };

    const handleFlip = () => {
        setIsTransitioning(false);
        setIsFlipped(!isFlipped);
        if (!isFlipped) onboarding.onCardFlipped(); // ← só quando vira (false → true)
    };

    // ✅ Remede a densidade do conteúdo sempre que o card muda ou vira — cobre
    // tanto o lado que acabou de renderizar quanto o que estava com
    // display:none (mobile) e só passou a ter altura real agora.
    useEffect(() => {
        remeasure();
    }, [algorithm.currentCard?.card._id, isFlipped, remeasure]);

    const toggleSupportContent = () => {
        const newState = !showSupportContent;
        setShowSupportContent(newState);

        if (newState) {
            setTimeout(() => {
                const supportSection = document.querySelector('.support-content-section');
                if (supportSection) {
                    supportSection.scrollIntoView({ behavior: 'smooth', block: 'start' });
                }
            }, 100);
        } else {
            window.scrollTo({ top: 0, behavior: 'smooth' });
        }
    };

    const handleReportError = async (errorType: string, errorDetails: string) => {
        try {
            const token = localStorage.getItem('accessToken');
            await fetch(
                `/api/decks/${deckId}/${subdeckId}/report-error`,
                {
                    method: 'POST',
                    headers: {
                        'Content-Type': 'application/json',
                        Authorization: `Bearer ${token}`
                    },
                    body: JSON.stringify({
                        cardId: algorithm.currentCard?.card._id,
                        errorType,
                        details: errorDetails
                    })
                }
            );

            setToast({ message: 'Erro reportado com sucesso!', type: 'success' });
        } catch (error) {
            console.error('Erro ao reportar:', error);
            setToast({ message: 'Erro ao enviar report', type: 'error' });
        }
    };

    const handleCardAdded = (message: string) => {
        setToast({ message, type: 'success' });
    };

    // =====================================================================
    // RENDERIZAÇÃO
    // =====================================================================

    const renderCardContent = useCallback((html: string) => {
        const tempDiv = document.createElement('div');
        tempDiv.innerHTML = html;
        tempDiv.querySelectorAll('.delete-img-btn').forEach(btn => btn.remove());
        return { __html: tempDiv.innerHTML };
    }, []);

    // ✅ Loading
    if ((algorithm.loading && !algorithm.currentCard) || (isReviewMode && algorithm.loadingReviewIds)) {
        return (
            <div className="study-page-final">
                <div className="skeleton skeleton-header" />
                <div style={{
                    display: 'flex',
                    justifyContent: 'center',
                    padding: '32px 24px'
                }}>
                    <div className="skeleton skeleton-card" style={{ maxWidth: '820px', width: '100%' }} />
                </div>
            </div>
        );
    }

    // ✅ Sem cards
    if (!algorithm.currentCard) {
        return (
            <div className="study-page-final">
                <div className="no-cards-final">
                    <BookX size={64} strokeWidth={1.5} style={{ color: '#0D3B0E', marginBottom: '16px' }} />
                    <h2>Nenhum card disponível</h2>
                    <button
                        className="btn-back-dashboard"
                        onClick={() => {
                            if (isUserContent) {
                                try {
                                    const ctx = JSON.parse(sessionStorage.getItem('userEditorContext') || '{}');
                                    navigate(ctx.collectionId ? `/my-flashcards/${ctx.collectionId}` : '/dashboard', {
                                        state: { activePage: 'explore', activeExploreTab: 'create-flashcards' }
                                    });
                                } catch {
                                    navigate('/dashboard', { state: { activePage: 'explore', activeExploreTab: 'create-flashcards' } });
                                }
                                return;
                            }
                            if (deckId) sessionStorage.removeItem(`subdecks_${deckId}`);
                            const dest = fromRyan && ryanReturnPath
                                ? ryanReturnPath
                                : isCustomStudy || isSavedMode
                                    ? '/dashboard'
                                    : `/subdecks/${deckId}`;
                            if (!dest.startsWith('/ryan')) sessionStorage.removeItem('graia_theme_context');
                            navigate(dest, fromRyan ? { state: { mode: ryanReturnMode, view: ryanReturnView, folderId: ryanReturnFolderId } } : {});
                        }}
                    >
                        ← {isUserContent ? 'Voltar para Decks' : fromRyan && ryanReturnPath ? 'Voltar para Ryan' : isCustomStudy || isSavedMode ? 'Voltar ao Painel Principal' : 'Voltar para Subdecks'}
                    </button>
                </div>
            </div>
        );
    }

    const hasSupportContent = !!algorithm.currentCard?.card.supportContent?.trim();

    // ✅ Renderização principal
    return (
        <div
            className={`study-page-final ${showSupportContent ? 'scrollable' : ''} ${isFocusMode ? 'focus-mode' : ''}`}
            data-theme="light"
        >
            {/* ========== HEADER VERDE INSTITUCIONAL ========== */}
            <div className="header-final">
                {/* Esquerda */}
                <div className="header-left">

                    <button
                        className="btn-close-final"
                        onClick={() => {
                            if (isFocusMode && shouldShowExitWarning()) {
                                setShowFocusExitModal(true);
                                return;
                            }

                            // Registrar cards vistos se for amostra gratuita (não aplica ao conteúdo do usuário)
                            if (!isAdmin && !isPurchasedDeck && !isUserContent && deckId && subdeckId) {
                                const cardsStudied = stats.sessionStats.cardsStudied;
                                if (cardsStudied > 0) {
                                    const token = localStorage.getItem('accessToken');
                                    fetch('/api/admin/free-config/record-view', {
                                        method: 'POST',
                                        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
                                        body: JSON.stringify({
                                            deckId,
                                            subdeckId,
                                            cardsViewed: cardsStudied,
                                            isCustom: isCustomPath
                                        })
                                    }).then(res => res.json()).then(data => {
                                        if (data.exhausted) {
                                            sessionStorage.setItem(`free_exhausted_${deckId}_${subdeckId}`, 'true');
                                        }
                                    }).catch(() => { });
                                }
                            }

                            if (!isUserContent && deckId) {
                                sessionStorage.removeItem(`subdecks_${deckId}`);
                            }

                            // Navegação de saída para conteúdo do usuário
                            if (isUserContent) {
                                try {
                                    const ctx = JSON.parse(sessionStorage.getItem('userEditorContext') || '{}');
                                    const collectionId = ctx.collectionId;
                                    navigate(collectionId ? `/my-flashcards/${collectionId}` : '/dashboard', {
                                        state: { activePage: 'explore', activeExploreTab: 'create-flashcards' }
                                    });
                                } catch {
                                    navigate('/dashboard', { state: { activePage: 'explore', activeExploreTab: 'create-flashcards' } });
                                }
                                return;
                            }

                            const backPath = isCustomPath
                                ? `/custom-decks/${deckId}/subdecks`
                                : fromRyan && ryanReturnPath
                                    ? ryanReturnPath
                                    : isSavedMode
                                        ? '/dashboard#saved'
                                        : isCustomStudy
                                            ? '/dashboard'
                                            : `/subdecks/${deckId}`;
                            subdeckProgress.saveCurrentSession();
                            if (!backPath.startsWith('/ryan')) sessionStorage.removeItem('graia_theme_context');
                            navigate(backPath, fromRyan ? { state: { mode: ryanReturnMode } } : {});
                        }}
                    >
                        ✕
                    </button>

                    <div className="deck-info-final">
                        <span className="deck-title-final">
                            {isCustomStudy ? (customStudyLabel || 'Estudo Personalizado') : deckName}
                        </span>
                        {subdeckName && <span className="subdeck-title-final">{subdeckName}</span>}
                    </div>
                </div>

                {isFocusMode ? (
                    <FocusTimer
                        timeRemaining={focusTimeRemaining}
                        isComplete={focusTimeRemaining === 0}
                        onAddTime={addFocusTime}
                    />
                ) : (!isAdmin && !isPurchasedDeck && freeCardsLimit !== null) ? (
                    <div className="free-sample-counter">
                        <div className="fsc-bar-wrap">
                            <div
                                className="fsc-bar-fill"
                                style={{ width: `${Math.min(100, ((freeCardsUsed) / freeCardsLimit) * 100)}%` }}
                            />
                        </div>
                        <span className="fsc-label">
                            {Math.max(0, freeCardsLimit - freeCardsUsed)} de {freeCardsLimit} cards gratuitos restantes
                        </span>
                    </div>
                ) : (
                    <div />
                )}

                {/* Direita - Domínio + Botão Sidebar */}
                <div style={{ display: 'flex', alignItems: 'center', gap: '16px' }}>
                    {!isCustomStudy && !isSavedMode && progressStats && (
                        <div className="mastery-bar-container">
                            {/* Esquerda: label + % empilhados */}
                            <div className="mastery-label-col">
                                <span className="mastery-label">Domínio</span>
                                <span className="mastery-percentage">{Math.round(displayedMastery)}%</span>
                            </div>
                            {/* Direita: barra + histórico */}
                            <div className="mastery-bar-wrapper-col">
                                <div className="mastery-bar-row">
                                    <div className="mastery-bar-bg">
                                        <div className="mastery-bar-fill" style={{ width: `${displayedMastery}%` }} />
                                    </div>
                                </div>
                                <CardRatingHistory
                                    key={algorithm.currentCard?.card._id}
                                    history={algorithm.currentCard?.progress?.ratingHistory || []}
                                />
                            </div>
                        </div>
                    )}

                    {/* ✅ Botão de Dark Mode */}
                    <button
                        className="btn-toggle-theme"
                        onClick={toggleTheme}
                        aria-label="Alternar tema"
                    >
                        {theme === 'dark' ? <Sun size={18} /> : <Moon size={18} />}
                    </button>

                    {/* ✅ Botão para abrir sidebar */}
                    <button
                        className="btn-toggle-sidebar"
                        onClick={() => setIsSidebarOpen(true)}
                        aria-label="Ver estatísticas"
                    >
                        <BarChart3 size={18} />
                    </button>
                </div>
            </div>

            {/* ========== MAIN CONTENT - CENTRALIZADO ========== */}
            <div className="main-content-final">
                <div className="card-area-final">
                    {/* Card */}
                    <div
                        className={`flashcard-final ${isFlipped ? 'flipped' : ''}`}
                        onClick={handleFlip}
                    >
                        {/* Menu de ações */}
                        <div onClick={() => !isUserContent && onboarding.onMenuOpened()}>
                            <CardActionsMenu
                                hasReferences={!!(algorithm.currentCard?.card.references?.trim() || subdeckReferences?.trim())}
                                onShowReferences={() => setShowReferencesModal(true)}
                                onReportError={() => setShowErrorModal(true)}
                                onReloadSavedCards={loadSavedCards}
                                onShowToast={(message, type) => setToast({ message, type })}
                                deckId={deckId || ''}
                                subdeckId={subdeckId || ''}
                                cardId={algorithm.currentCard?.card._id}
                                cardContent={{
                                    front: algorithm.currentCard?.card.front,
                                    back: algorithm.currentCard?.card.back,
                                    supportContent: algorithm.currentCard?.card.supportContent,
                                    references: algorithm.currentCard?.card.references
                                }}
                                hierarchy={{
                                    cycleName: null,
                                    cycleId: null,
                                    deckName: deckName,
                                    subdeckName: subdeckName
                                }}
                                onToggleSupportContent={toggleSupportContent}
                                hasSupportContent={hasSupportContent}
                                fromRyan={fromRyan}
                                ryanSpaceName={ryanSpaceName}
                                ryanSpaceId={ryanSpaceId}
                                hideActionsMenu={isUserContent}
                            />
                        </div>

                        {/* FRENTE */}
                        <div className="flashcard-front">
                            <div className="card-content-wrapper">
                                <div className="card-content">
                                    <div
                                        ref={frontContentRef}
                                        onClick={(e) => {
                                            const target = e.target as HTMLElement;
                                            if (target.tagName === 'IMG') {
                                                e.stopPropagation();
                                                setZoomImage((target as HTMLImageElement).src);
                                                return;
                                            }
                                            handleFlip();
                                        }}
                                        className={isTransitioning ? 'content-transitioning' : ''}
                                        dangerouslySetInnerHTML={renderCardContent(
                                            algorithm.currentCard?.card.front
                                        )}
                                    />

                                    <CardMediaRenderer
                                        imageUrl={algorithm.currentCard?.card.media?.images?.find(
                                            img => img.side === 'front'
                                        )?.url}
                                        audioUrl={algorithm.currentCard?.card.media?.audios?.find(
                                            aud => aud.side === 'front'
                                        )?.url}
                                        onImageClick={(url) => setZoomImage(url)}
                                    />
                                </div>
                            </div>
                        </div>

                        {/* VERSO */}
                        <div className="flashcard-back">
                            <div className="card-content-wrapper">
                                <div className="card-content">
                                    <div
                                        ref={backContentRef}
                                        onClick={(e) => {
                                            const target = e.target as HTMLElement;
                                            if (target.tagName === 'IMG') {
                                                e.stopPropagation();
                                                setZoomImage((target as HTMLImageElement).src);
                                                return;
                                            }
                                            handleFlip();
                                        }}
                                        className={isTransitioning ? 'content-transitioning' : ''}
                                        dangerouslySetInnerHTML={renderCardContent(
                                            algorithm.currentCard?.card.back
                                        )}
                                    />

                                    <CardMediaRenderer
                                        imageUrl={algorithm.currentCard?.card.media?.images?.find(
                                            img => img.side === 'back'
                                        )?.url}
                                        audioUrl={algorithm.currentCard?.card.media?.audios?.find(
                                            aud => aud.side === 'back'
                                        )?.url}
                                        onImageClick={(url) => setZoomImage(url)}
                                    />
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* ========== CONTROLES COM HIERARQUIA VISUAL ========== */}
                    <div className="controls-area">
                        {!isFlipped ? (
                            <div className="question-controls">
                                <button
                                    className="control-btn btn-back-inline"
                                    onClick={algorithm.handleGoBack}
                                    disabled={algorithm.stackIndex <= 0}
                                >
                                    ← Voltar
                                </button>

                                <button className="control-btn btn-show-answer-inline" onClick={handleFlip}>
                                    Ver resposta
                                </button>

                                <button
                                    className="control-btn btn-skip-inline"
                                    onClick={algorithm.handleSkip}
                                    disabled={algorithm.isAnswering}
                                >
                                    Pular →
                                </button>
                            </div>
                        ) : (
                            <div className="answer-controls">
                                <p className="evaluation-text">Como você avalia seu conhecimento?</p>
                                <div className="rating-buttons-inline">
                                    {[1, 2, 3, 4, 5].map(rating => (
                                        <button
                                            key={rating}
                                            className={`rating-btn-inline level-${rating}`}
                                            onClick={(e) => {
                                                e.stopPropagation();
                                                handleAnswer(rating);
                                            }}
                                            disabled={algorithm.isAnswering}
                                        >
                                            <span className="rating-num">{rating}</span>
                                            <span className="rating-txt">
                                                {rating === 1 && 'Muito\nDifícil'}
                                                {rating === 2 && 'Difícil'}
                                                {rating === 3 && 'Bom'}
                                                {rating === 4 && 'Fácil'}
                                                {rating === 5 && 'Muito\nFácil'}
                                            </span>
                                        </button>
                                    ))}
                                </div>
                            </div>
                        )}
                    </div>
                </div>
            </div>

            {/* ========== SIDEBAR SOB DEMANDA ========== */}
            <StudySidebarPanel
                isOpen={isSidebarOpen}
                onClose={() => setIsSidebarOpen(false)}
                cardsStudied={stats.sessionStats.cardsStudied}
                correctAnswers={stats.correctAnswers}
                precision={stats.precision}
                ratings={{
                    1: stats.sessionStats.ratings[1].count,
                    2: stats.sessionStats.ratings[2].count,
                    3: stats.sessionStats.ratings[3].count,
                    4: stats.sessionStats.ratings[4].count,
                    5: stats.sessionStats.ratings[5].count
                }}
                progressData={!isSavedMode && !isCustomStudy ? subdeckProgress.data : undefined}
                progressLoading={subdeckProgress.loading}
                onOpenProgressTab={subdeckProgress.fetchProgressData}
            />

            {/* Conteúdo de Apoio */}
            <SupportContent
                isVisible={showSupportContent}
                content={algorithm.currentCard?.card.supportContent || ''}
                onClose={() => setShowSupportContent(false)}
                onImageClick={(imageUrl) => {
                    setSupportZoomImage(imageUrl);
                    setSupportZoomScale(1);
                }}
            />

            {/* Modais */}
            <ImageZoomModal
                imageUrl={zoomImage}
                onClose={() => {
                    setZoomImage(null);
                    setZoomScale(1);
                }}
                zoomScale={zoomScale}
                onZoomChange={setZoomScale}
            />

            <ImageZoomModal
                imageUrl={supportZoomImage}
                onClose={() => setSupportZoomImage(null)}
                zoomScale={supportZoomScale}
                onZoomChange={setSupportZoomScale}
            />

            <ReportErrorModal
                isOpen={showErrorModal}
                onClose={() => setShowErrorModal(false)}
                onSubmit={handleReportError}
            />

            <FocusExitModal
                isOpen={showFocusExitModal}
                onClose={() => setShowFocusExitModal(false)}
                onConfirmExit={() => confirmExit(() => { })}
                isSavedMode={isSavedMode}
                isCustomStudy={isCustomStudy}
                isCustomPath={isCustomPath}
                deckId={deckId}
            />

            {algorithm.currentCard && (
                <QuickAddModal
                    isOpen={showQuickAddModal}
                    onClose={() => setShowQuickAddModal(false)}
                    currentCard={{
                        _id: algorithm.currentCard?.card._id,
                        deckId: algorithm.currentCard?.card.deckId || deckId || '',
                        subdeckId: algorithm.currentCard?.card.subdeckId || subdeckId || '',
                        front: algorithm.currentCard?.card.front,
                        back: algorithm.currentCard?.card.back
                    }}
                    onSuccess={handleCardAdded}
                />
            )}

            <ReferencesStudy
                isOpen={showReferencesModal}
                onClose={() => setShowReferencesModal(false)}
                references={
                    algorithm.currentCard?.card.references?.trim()
                        ? algorithm.currentCard.card.references
                        : subdeckReferences
                }
            />

            {toast && (
                <Toast
                    message={toast.message}
                    type={toast.type}
                    onClose={handleCloseToast}
                />
            )}

            {/* ← ADICIONAR AQUI */}
            <StudyOnboarding
                currentStep={onboarding.currentStep}
                isVisible={onboarding.isVisible}
                onDismissWelcome={onboarding.dismissWelcome}
                onDismissStep={onboarding.dismissStep}
            />

            <SampleExhaustedModal
                isOpen={showSampleExhaustedModal}
                onGoBack={() => {
                    if (deckId) sessionStorage.removeItem(`subdecks_${deckId}`);
                    const backPath = isCustomPath
                        ? `/custom-decks/${deckId}/subdecks`
                        : fromRyan && ryanReturnPath
                            ? ryanReturnPath
                            : `/subdecks/${deckId}`;
                    if (!backPath.startsWith('/ryan')) sessionStorage.removeItem('graia_theme_context');
                    navigate(backPath, fromRyan ? { state: { mode: ryanReturnMode, view: ryanReturnView, folderId: ryanReturnFolderId } } : {});
                }}
            />
        </div>
    );
};

export default StudyPage;